import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { DatePicker } from 'primeng/datepicker';
import { Editor } from 'primeng/editor';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { MessageService, SharedModule } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, EMPTY, filter, finalize, map, tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ControlErrorDirective } from '../../../../../shared/directive/control-error.directive';
import { QuillListMarkerStyleDirective } from '../../../../../shared/directive/quill-list-marker-style.directive';
import { RemoveQuillTooltipDirective } from '../../../../../shared/directive/remove-quill-tooltip.directive';
import { RequiredLabelDirective } from '../../../../../shared/directive/required-label.directive';
import { AnnouncementAddFormBuilder } from './form-group/announcement-add-form.builder';
import { AnnouncementAddFormModel } from './form-group/announcement-add-form.model';
import { AnnouncementChannel } from '../../../../../shared/enums/announcement-channel.enum';
import { AnnouncementRecipientType } from '../../../../../shared/enums/announcement-recipient-type.enum';
import { FormsUtils } from '../../../../../shared/utils/forms.utils';
import { APP_PATHS } from '../../../../../core/config/app-paths';
import { ToastSeverity } from '../../../../../shared/enums/toast-severity.enum';
import { AnnouncementRecipientDialog } from '../announcement-recipient-dialog/announcement-recipient-dialog';
import { AnnouncementRecipient } from '../../../../../shared/models/announcement-recipient';
import { AnnouncementsApi } from '../../../../../shared/services/api/announcements.api';
import { AnnouncementCreateRequest } from '../../../../../shared/models/announcement-create-request';
import {
  ANNOUNCEMENT_CHANNEL_OPTIONS,
  ANNOUNCEMENT_CONFIRMATION_OPTIONS,
  ANNOUNCEMENT_RECIPIENT_TYPE_OPTIONS,
} from '../../../../../shared/constants/announcement-form-options';
import { DateValidators } from '../../../../../shared/validators/date-validators';
import { SharedValidators } from '../../../../../shared/validators/shared-validators';
import { AnnouncementConfirmationRequirement } from '../../../../../shared/enums/announcement-confirmation-requirement.enum';
import { AnnouncementRecipientsComponent } from '../../components/announcement-recipients/announcement-recipients.component';
import { SpinnerService } from '../../../../../core/services/spinner.service';
import { HttpResponseService } from '../../../../../core/services/http-error-response.service';
import { DokumentApi } from '../../../../../shared/services/api/dokument.api';

@Component({
  selector: 'app-admin-panel-announcement-add',
  imports: [
    Button,
    Card,
    ControlErrorDirective,
    DatePicker,
    Editor,
    InputText,
    QuillListMarkerStyleDirective,
    RemoveQuillTooltipDirective,
    ReactiveFormsModule,
    RequiredLabelDirective,
    Select,
    SharedModule,
    AnnouncementRecipientsComponent,
  ],
  templateUrl: './admin-panel-announcement-add.html',
  styleUrl: './admin-panel-announcement-add.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AnnouncementsApi],
})
export class AdminPanelAnnouncementAdd {
  protected formGroup = signal<FormGroup<AnnouncementAddFormModel> | null>(
    AnnouncementAddFormBuilder.createFormGroup(),
  );
  protected readonly channelOptions = ANNOUNCEMENT_CHANNEL_OPTIONS;
  protected readonly confirmationOptions = ANNOUNCEMENT_CONFIRMATION_OPTIONS;
  protected readonly recipientTypeOptions = ANNOUNCEMENT_RECIPIENT_TYPE_OPTIONS;
  protected readonly today = new Date();
  protected readonly recipients = signal<AnnouncementRecipient[]>([]);
  readonly #router = inject(Router);
  readonly #messageService = inject(MessageService);
  readonly #dialogService = inject(DialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #announcementsApi = inject(AnnouncementsApi);
  readonly #dokumentApi = inject(DokumentApi);
  protected readonly requestTypeOptions = toSignal(
    this.#dokumentApi.getDokumentTypesCodes().pipe(
      map((data) =>
        data.map((value) => ({
          label: value,
          value: value,
        })),
      ),
      catchError((err) => {
        this.#httpResponseService.showErrorMessage(
          err,
          'Wystąpił błąd podczas pobierania niezbędnych danych do wypełnienia formularza',
        );
        return EMPTY;
      }),
    ),
    { initialValue: [] },
  );
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);

  constructor() {
    this.#initConditionalValidators();
    this.#applyDateValidators();
  }

  protected shouldShowSystemFields(): boolean {
    const channel = this.formGroup()?.controls.channel.value;
    return (
      channel === AnnouncementChannel.SYSTEM ||
      channel === AnnouncementChannel.SYSTEM_I_EMAIL
    );
  }

  protected shouldShowEmailFields(): boolean {
    const channel = this.formGroup()?.controls.channel.value;
    return (
      channel === AnnouncementChannel.EMAIL ||
      channel === AnnouncementChannel.SYSTEM_I_EMAIL
    );
  }

  protected shouldShowRecipients(): boolean {
    return (
      this.formGroup()?.controls.recipientType.value ===
      AnnouncementRecipientType.INDYWIDUALNY
    );
  }

  protected shouldShowRequestType(): boolean {
    return (
      this.formGroup()?.controls.recipientType.value ===
      AnnouncementRecipientType.WNIOSEK
    );
  }

  protected onSelectRecipients(): void {
    const ref = this.#dialogService.open(AnnouncementRecipientDialog, {
      header: 'Wybierz adresata',
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      width: '1100px',
      data: {
        selectedRecipients: this.formGroup()?.controls.recipients.value ?? [],
      },
      breakpoints: {
        '960px': '95vw',
        '640px': '95vw',
      },
    });

    if (!ref) {
      return;
    }

    ref.onClose
      .pipe(filter(Boolean), takeUntilDestroyed(this.#destroyRef))
      .subscribe((recipients: AnnouncementRecipient[]) => {
        this.recipients.set(recipients);
        this.formGroup()?.controls.recipients.setValue(recipients);
        this.formGroup()?.controls.recipients.updateValueAndValidity({
          emitEvent: false,
        });
        this.#cdr.markForCheck();
      });
  }

  protected onRemoveRecipient(recipientToRemove: AnnouncementRecipient): void {
    const recipients = this.recipients().filter(
      ({ email }) => email !== recipientToRemove.email,
    );
    this.recipients.set(recipients);
    this.formGroup()?.controls.recipients.setValue(recipients);
    this.formGroup()?.controls.recipients.updateValueAndValidity({
      emitEvent: false,
    });
    this.#cdr.markForCheck();
  }

  protected onSave(): void {
    if (!this.formGroup() || !FormsUtils.isReadyForSave(this.formGroup()!)) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Wystąpił błąd podczas walidacji formularza',
      });
      return;
    }

    const payload =
      this.formGroup()!.getRawValue() as AnnouncementCreateRequest;

    this.#spinnerService.show();
    this.#announcementsApi
      .createAnnouncement$(payload)
      .pipe(
        tap((value) => {
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie dodano komunikat.',
          );
          this.#router.navigate([
            `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}/${value.id}/${APP_PATHS.admin.details}`,
          ]);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby dodania komunikatu.',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected onCancel(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}`,
    ]);
  }

  #initConditionalValidators(): void {
    if (!this.formGroup()) {
      return;
    }

    this.formGroup()!
      .controls.channel.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#applyConditionalValidators();
        this.#applyConditionalRules();
      });

    this.formGroup()!
      .controls.recipientType.valueChanges.pipe(
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.#applyConditionalValidators();
        this.#applyConditionalRules();
        this.formGroup()!.controls.recipients.updateValueAndValidity({ emitEvent: false });
      });

    this.#applyConditionalValidators();
    this.#applyConditionalRules();
    this.formGroup()!.updateValueAndValidity({ emitEvent: false });
  }

  #applyConditionalRules(): void {
    if (!this.formGroup()) {
      return;
    }

    if (!this.shouldShowSystemFields()) {
      this.formGroup()!.controls.requiresConfirmation.setValue(
        AnnouncementConfirmationRequirement.NIE_DOTYCZY,
        { emitEvent: false },
      );
      this.#clearControl(this.formGroup()!.controls.startDate);
      this.#clearControl(this.formGroup()!.controls.endDate);
    } else if (
      this.formGroup()!.controls.requiresConfirmation.value ===
      AnnouncementConfirmationRequirement.NIE_DOTYCZY
    ) {
      this.formGroup()!.controls.requiresConfirmation.setValue(null, {
        emitEvent: false,
      });
    }

    if (!this.shouldShowEmailFields()) {
      this.#clearControl(this.formGroup()!.controls.plannedEmailSendAt);
    }

    if (!this.shouldShowRecipients()) {
      this.recipients.set([]);
      this.#clearControl(this.formGroup()!.controls.recipients);
    }

    if (!this.shouldShowRequestType()) {
      this.#clearControl(this.formGroup()!.controls.requestCode);
    }
  }

  #applyConditionalValidators(): void {
    if (!this.formGroup()) {
      return;
    }

    this.formGroup()!.controls.requiresConfirmation.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowSystemFields(),
      ),
    );
    this.formGroup()!.controls.startDate.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowSystemFields(),
      ),
    );
    this.formGroup()!.controls.endDate.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowSystemFields(),
      ),
    );
    this.formGroup()!.controls.plannedEmailSendAt.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowEmailFields(),
      ),
    );
    this.formGroup()!.controls.recipients.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowRecipients(),
      ),
    );
    this.formGroup()!.controls.requestCode.addValidators(
      SharedValidators.when(Validators.required, () =>
        this.shouldShowRequestType(),
      ),
    );
  }

  #applyDateValidators(): void {
    if (!this.formGroup()) {
      return;
    }

    this.formGroup()!.setValidators([
      DateValidators.startNotAfterEnd('startDate', 'endDate'),
    ]);

    this.formGroup()!.controls.plannedEmailSendAt.addValidators(
      DateValidators.notInPast(),
    );
  }

  #clearControl(control: AbstractControl): void {
    control.setValue(null, { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
  }
}
